import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "./ui/badge";
import { Eye, MessageSquare, TrendingDown, TrendingUp, BarChart3, Heart, Zap, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

function Suggestion({ listingId, s, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${listingId}/price-suggestion/accept`, {});
      toast.success(`Price dropped to $${Number(data.asking_price).toLocaleString()} — ${data.notified} interested buyer${data.notified === 1 ? "" : "s"} notified`);
      onAccepted(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update price"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid={`suggestion-${listingId}`}>
      <Zap className="w-4 h-4 text-amber-300 shrink-0" />
      <div className="text-xs flex-1 min-w-[200px]">
        <p className="text-amber-200 font-semibold">{s.reason}. Recommended: <span className="text-white">${Number(s.recommended_price).toLocaleString()}</span> <span className="text-slate-400">(−${Number(s.drop).toLocaleString()}, {s.basis})</span></p>
        <p className="text-slate-400 mt-0.5">{s.interested_buyers} interested buyer{s.interested_buyers === 1 ? "" : "s"} will be emailed about the drop.</p>
      </div>
      <Button size="sm" onClick={accept} disabled={busy} data-testid={`accept-suggestion-${listingId}`} className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Drop to $${Number(s.recommended_price).toLocaleString()}`}
      </Button>
    </div>
  );
}

function Spark({ data }) {
  const max = Math.max(1, ...data);
  return (
    <div className="flex items-end gap-0.5 h-8" data-testid="views-spark">
      {data.map((v, i) => <div key={i} className="w-2 rounded-sm bg-cyan-500/70" style={{ height: `${Math.max(8, (v / max) * 100)}%` }} title={`${v} views`} />)}
    </div>
  );
}

export default function SellerStats() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/my/sales/stats").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid="seller-stats-section">
      <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-cyan-400" /> Listing performance</h2>
      <div className="space-y-3">
        {rows.map((r) => {
          const vs7 = r.views_7d.reduce((a, b) => a + b, 0);
          const diff = r.instant_offer ? r.asking_price - r.instant_offer : null;
          const ppv = r.private_party_value;
          const tip = r.suggestion ? null : r.status !== "active" ? null
            : r.buyer_messages >= 3 ? "Strong interest — hold your price." : null;
          return (
            <div key={r.listing_id} className="p-4 rounded-2xl bg-[#131722] border border-slate-800" data-testid={`stat-${r.listing_id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="w-14 h-10 rounded-lg overflow-hidden bg-slate-900 shrink-0">{r.photo && <img src={r.photo} alt="" className="w-full h-full object-cover" />}</div>
                <Link to={`/buy/${r.listing_id}`} className="font-semibold hover:text-amber-300">{r.label}</Link>
                <Badge className="bg-slate-800 text-slate-200 text-[10px] uppercase">{r.status}</Badge>
                <span className="text-xs text-slate-500 font-mono">{r.days_listed}d listed</span>
                {r.hearts > 0 && <span className="text-xs text-pink-300 flex items-center gap-1" data-testid={`hearts-stat-${r.listing_id}`}><Heart className="w-3 h-3 fill-current" />{r.hearts}</span>}
                <span className="ml-auto font-display font-extrabold">${Number(r.asking_price).toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800"><p className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" />Views</p><p className="font-bold text-lg" data-testid={`views-${r.listing_id}`}>{r.views}</p><p className="text-[11px] text-slate-500">{vs7} last 7d</p><Spark data={r.views_7d} /></div>
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800"><p className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Buyers</p><p className="font-bold text-lg" data-testid={`threads-${r.listing_id}`}>{r.threads}</p><p className="text-[11px] text-slate-500">{r.buyer_messages} buyer msgs · {r.messages} total</p></div>
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800"><p className="text-[10px] font-mono uppercase text-slate-500">Offer vs asking</p>
                  {r.instant_offer ? <><p className="font-bold text-lg flex items-center gap-1" data-testid={`offer-gap-${r.listing_id}`}>{diff >= 0 ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}+${Number(diff).toLocaleString()}</p><p className="text-[11px] text-slate-500">cash offer ${Number(r.instant_offer).toLocaleString()}{ppv && ` · market ≈ $${Number(ppv).toLocaleString()}`}</p></> : <p className="text-xs text-slate-500 mt-1">No instant offer on file</p>}
                </div>
                <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800"><p className="text-[10px] font-mono uppercase text-slate-500">Price trend</p><p className="font-bold text-lg">{r.price_changes === 0 ? "—" : `${r.price_changes} change${r.price_changes > 1 ? "s" : ""}`}</p><p className="text-[11px] text-slate-500">{r.original_price !== r.asking_price ? `from $${Number(r.original_price).toLocaleString()}` : "unchanged since listing"}</p></div>
              </div>
              {r.suggestion && <Suggestion listingId={r.listing_id} s={r.suggestion} onAccepted={load} />}
              {tip && <p className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2" data-testid={`tip-${r.listing_id}`}>{tip}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
